import React, { useState, useEffect, useCallback } from 'react';

const DEFAULT_SECTIONS = [
  {
    id: 'price',
    title: 'Price',
    type: 'checkbox',
    options: [
      { key: 'free', label: 'Free' },
      { key: '0-9', label: 'Under $9.99' },
  { key: '10-20', label: '$10 - $20' },
      { key: '20-29', label: '$20.00-$29.99' },
      { key: '30+', label: '$30.00+' },
    ],
  },
  {
    id: 'rating',
    title: 'Rating',
    type: 'checkbox',
    options: [
      { key: '1', label: '1+ Stars' },
      { key: '2', label: '2+ Stars' },
      { key: '3', label: '3+ Stars' },
      { key: '4', label: '4+ Stars' },
    ],
  },
  {
    id: 'genre',
    title: 'Genre',
    type: 'checkbox',
    options: [
      { key: 'Sports', label: 'Sports' },
      { key: 'Action', label: 'Action' },
      { key: 'Puzzle', label: 'Puzzle' },
      { key: 'Strategy', label: 'Strategy' },
    ],
  },
  {
    id: 'sort',
    title: 'Sort by',
    type: 'radio',
    options: [
      { key: '-rating', label: 'Rating (High → Low)' },
      { key: 'rating', label: 'Rating (Low → High)' },
      { key: '-price', label: 'Price (High → Low)' },
      { key: 'price', label: 'Price (Low → High)' },
      { key: '-downloads', label: 'Downloads (High → Low)' },
      { key: 'downloads', label: 'Downloads (Low → High)' },
      { key: '-revenue', label: 'Revenue (High → Low)' },
      { key: 'revenue', label: 'Revenue (Low → High)' },
      { key: '-release_date', label: 'Newest first' },
      { key: 'release_date', label: 'Oldest first' },
    ],
  },
  {
    id: 'bundle',
    title: 'Bundle',
    type: 'checkbox',
    options: [
      { key: 'Games', label: 'Games' },
      { key: 'Productivity', label: 'Productivity' },
      { key: 'Entertainment', label: 'Entertainment' },
    ],
  },
  {
    id: 'number_of_players',
    title: 'Number of players',
    type: 'radio',
    options: [
      { key: 'Single User', label: 'Single User' },
      { key: 'Multiplayer', label: 'Multi player' },
      { key: 'Co-op', label: 'Co-op' },
    ],
  },
];

const FilterPanel = ({ open = false, onClose = () => {}, onApply = () => {}, sections = DEFAULT_SECTIONS, lockedOptions = [], onLockClick = () => {}, initialValues = {}, anchorRef = null, leftOffset = 0, centerPosition = false, gap = 0 }) => {
  const [visible, setVisible] = useState(open);
  const [expanded, setExpanded] = useState([]); // start collapsed, will expand dynamically
  const [values, setValues] = useState({}); // Start with empty state
  const [panelPosition, setPanelPosition] = useState({ top: 112, right: '20px', left: 'auto', transform: 'none' });

  // The panel never renders its own "Platform" section anymore — every caller of
  // this panel already has a platform tab row/selector outside it (Genre/Publisher/
  // Keyword Explorer, UGC Explorer, Ad Discovery's "Search Apps & Games" modal,
  // etc.), so a duplicate checkbox list here was always redundant.
  const allSections = sections;

  // Helper: remove empty entries (empty arrays, null/undefined/empty string)
  const sanitizeFilters = (filters) => {
    if (!filters || typeof filters !== 'object') return {}
    const out = {}
    for (const [k, v] of Object.entries(filters)) {
      if (Array.isArray(v)) {
        if (v.length > 0) out[k] = v
      } else if (v !== undefined && v !== null && v !== '') {
        out[k] = v
      }
    }
    return out
  }

  useEffect(() => setVisible(open), [open]);

  // Sync the panel's own checkbox/radio state from whatever the caller says is
  // currently applied, every time the panel opens. Without this, `values` was
  // pure internal state that only ever changed from clicks inside the panel -
  // so clearing filters from outside it (e.g. the page's "Clear All" pill)
  // updated the caller's data/URL/chips just fine, but reopening the panel
  // still showed the old options checked, since nothing had ever told this
  // component's own state to forget them.
  useEffect(() => {
    if (open) setValues(initialValues || {});
    // Deliberately only [open]: re-syncing on every initialValues change would
    // also clobber selections the user is actively making while the panel is
    // still open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Calculate panel position based on anchorRef
  const updatePanelPosition = useCallback(() => {
    if (open && anchorRef?.current) {
      const buttonRect = anchorRef.current.getBoundingClientRect();
      const panelWidth = 380; // Panel width in pixels
      
      if (centerPosition) {
        // Centered positioning for dashboard (30% viewport center, 70% button center)
        const viewportWidth = window.innerWidth;
        const padding = 20; // Minimum padding from viewport edges
        const viewportCenter = viewportWidth / 2;
        const buttonCenter = buttonRect.left + (buttonRect.width / 2);
        const weightedCenter = (viewportCenter * 0.3) + (buttonCenter * 0.7);
        let panelLeft = weightedCenter - (panelWidth / 2);
        
        // Ensure panel doesn't go off-screen
        panelLeft = Math.max(padding, Math.min(panelLeft, viewportWidth - panelWidth - padding));
        
        setPanelPosition({
          top: buttonRect.bottom + gap, // Position below button with gap
          left: panelLeft, // Shifted towards viewport center
          right: 'auto',
          transform: 'none'
        });
      } else {
        // Position panel to avoid going off-screen
        const viewportWidth = window.innerWidth;
        const padding = 20; // Minimum padding from viewport edges
        let panelLeft = buttonRect.left - leftOffset;
        
        // Check if panel would go off the right edge
        if (panelLeft + panelWidth > viewportWidth - padding) {
          // Align right edge of panel with button's right edge instead
          panelLeft = buttonRect.right - panelWidth + leftOffset;
          
          // Ensure panel doesn't go off the left edge either
          if (panelLeft < padding) {
            panelLeft = padding;
          }
        }
        
        setPanelPosition({
          top: buttonRect.bottom + gap, // Position below button with gap
          left: panelLeft,
          right: 'auto',
          transform: 'none'
        });
      }
    } else if (!anchorRef?.current) {
      setPanelPosition({ top: 112, right: '20px', left: 'auto', transform: 'none' });
    }
  }, [open, anchorRef, leftOffset, centerPosition, gap]);

  // Initial position calculation
  useEffect(() => {
    if (open) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        updatePanelPosition();
      });
    }
  }, [open, updatePanelPosition]);

  // Add scroll listener to update position when scrolling
  useEffect(() => {
    if (open && anchorRef?.current) {
      const handleScroll = () => {
        updatePanelPosition();
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', updatePanelPosition);

      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', updatePanelPosition);
      };
    }
  }, [open, anchorRef, updatePanelPosition]);

  // Expand all sections when panel opens
  useEffect(() => {
    if (open) {
      setExpanded(allSections.map(s => s.id));
    }
  }, [open, allSections]);

  // Determine whether any filter is currently selected. If none are selected,
  // the Apply button should be disabled.
  const hasAnySelection = (vals, secs) => {
    if (!vals) return false
    // Check known sections first
    for (const s of (secs || [])) {
      const v = vals[s.id]
      if (s.type === 'radio') {
        if (v !== undefined && v !== null && v !== '') return true
      } else {
        if (Array.isArray(v) && v.length > 0) return true
      }
    }
    // Fallback: check any keys in vals
    for (const k of Object.keys(vals || {})) {
      const v = vals[k]
      if (Array.isArray(v) && v.length > 0) return true
      if (!Array.isArray(v) && v !== undefined && v !== null && v !== '') return true
    }
    return false
  }

  const applyDisabled = !hasAnySelection(values, allSections)

  const toggleExpand = (id) => {
    setExpanded(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  };

  const onToggleOption = (section, key) => {
    setValues(prev => {
      const cur = prev[section.id];
      if (section.type === 'radio') {
        return { ...prev, [section.id]: cur === key ? null : key };
      }
      // checkbox
      const set = new Set(Array.isArray(cur) ? cur : []);
      if (set.has(key)) set.delete(key); else set.add(key);
      return { ...prev, [section.id]: Array.from(set) };
    });
  };

  const handleClear = () => {
    setValues({});
    // Actually apply the cleared filters (pass sanitized empty object)
    onApply({});
    setVisible(false);
    onClose();
  };

  const handleApply = () => {
    // Sanitize values before returning so callers get a clean object
    const clean = sanitizeFilters(values || {})
    onApply(clean);
    setVisible(false);
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="filter-panel-portal">
      <div className="filter-panel-backdrop" onClick={() => { setVisible(false); onClose(); }} />
      <div className="filter-panel" role="dialog" aria-modal>
        <div className="filter-panel-header">
          <h3>Filters</h3>
          <button className="close-x" onClick={() => { setVisible(false); onClose(); }} aria-label="Close">✕</button>
        </div>

        <div className="filter-panel-body">
          {allSections.map(section => (
            <div key={section.id} className="filter-section">
              <div className="filter-section-header" onClick={() => toggleExpand(section.id)}>
                <div>{section.title}</div>
                <div className="chev">{expanded.includes(section.id) ? '▾' : '▸'}</div>
              </div>
              {expanded.includes(section.id) && (
                <div className="filter-section-content">
                  {section.options.map(opt => {
                    const sel = values[section.id];
                    const checked = section.type === 'radio' ? sel === opt.key : Array.isArray(sel) && sel.includes(opt.key);
                    // Determine if this option should be treated as locked for UI only.
                    const optKeyNormalized = String(opt.key).replace(/^-/, '');
                    const isLocked = lockedOptions.includes(optKeyNormalized) || lockedOptions.includes(opt.key);
                    return (
                      <div 
                        className={`filter-item ${isLocked ? 'locked' : ''}`} 
                        key={opt.key}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: isLocked ? 'default' : 'pointer' }}
                        onClick={() => {
                          if (!isLocked) {
                            onToggleOption(section, opt.key);
                          }
                        }}
                      >
                        <div 
                          style={{
                            width: 18,
                            height: 18,
                            border: `2px solid ${!!checked ? '#F6255A' : '#6b7280'}`,
                            borderRadius: section.type === 'radio' ? '50%' : '4px',
                            backgroundColor: !!checked ? '#F6255A' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          {section.type === 'checkbox' && !!checked && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                          {section.type === 'radio' && !!checked && (
                            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'white' }}/>
                          )}
                        </div>
                        <span className="filter-item-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="filter-item-text">{opt.label}</span>
                          {isLocked && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onLockClick(opt); }}
                              aria-label="Locked feature - upgrade"
                              className="filter-lock-btn"
                              title="Upgrade to unlock"
                              style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                            >
                              {/* white lock icon */}
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17 8h-1V6a4 4 0 10-8 0v2H7a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1V9a1 1 0 00-1-1zm-7-2a2 2 0 114 0v2h-4V6z" fill="#FFFFFF"/>
                              </svg>
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="filter-panel-footer">
          <button className="clear-full-btn" onClick={handleClear}>Clear filters</button>
          <button className="apply-btn" onClick={handleApply} disabled={applyDisabled}>Apply</button>
        </div>

      </div>

      <style>{`
        .filter-panel-portal{ position:fixed; inset:0; z-index:60; }
        .filter-panel-backdrop{ position:absolute; inset:0; background:rgba(0,0,0,0.18); }
  /* keep panel below the app top header so header stays visible */
  /* dark theme to match the app layout */
  .filter-panel{ 
    position: absolute; 
    ${typeof panelPosition.left === 'number' 
      ? `left: ${panelPosition.left}px; transform: ${panelPosition.transform || 'none'}; right: auto;` 
      : panelPosition.left === 'auto' 
        ? `right: ${panelPosition.right || '20px'}; left: auto; transform: none;` 
        : `left: ${panelPosition.left}; transform: ${panelPosition.transform || 'none'}; right: auto;`
    } 
    top: ${panelPosition.top}px; 
    width:380px; 
    max-height: 450px; 
    background: #0f1114; 
    color:#e6edf3; 
    border-radius:28px; 
    box-shadow:0 22px 48px rgba(2,6,23,0.28); 
    display:flex; 
    flex-direction:column; 
    overflow:hidden; 
  }
  .filter-panel-header{ display:flex; align-items:center; justify-content:space-between; padding:26px 28px 6px 28px; border-bottom:1px solid rgba(255,255,255,0.04); }
  .filter-panel-header h3{ margin:0; font-size:26px; font-weight:600; color:#e6edf3 }
  .close-x{ background:transparent; border:none; font-size:22px; cursor:pointer; color:#cbd5e1; padding:6px; border-radius:10px }
  .close-x:hover{ background:rgba(255,255,255,0.02) }
  .filter-panel-body{ padding:18px 28px; overflow:auto; }
        .filter-section{ border-bottom:1px solid rgba(0,0,0,0.04); padding:10px 0; }
  .filter-section-header{ display:flex; justify-content:space-between; align-items:center; padding:8px 0; cursor:pointer; color:#d1d5db; font-weight:500; font-size:15px }
  .filter-section-header .chev{ color:#9aa5b2 }
  .filter-section-content{ padding:8px 0 14px 6px; display:flex; flex-direction:column; gap:12px }
  .filter-item{ display:flex; align-items:center; gap:12px; color:#dbe7f0; font-size:15px; cursor:pointer; pointer-events:auto; }
  .filter-item input{ width:18px; height:18px; accent-color:#F6255A; cursor:pointer; pointer-events:auto; }
  .filter-item.locked {
    /* make locked options visually de-emphasized by blurring the text */
  }
  .filter-item.locked .filter-item-text {
    /* stronger blur to match stats card locked treatment */
    filter: blur(6px);
    opacity: 0.5;
  }
  .filter-item.locked .filter-lock-btn {
    /* ensure lock icon remains sharp and visible */
    margin-left: 6px;
    transform: translateY(-1px);
  }
  .filter-panel-footer{ display:flex; justify-content:center; padding:18px 24px; border-top:1px solid rgba(255,255,255,0.03); }
  .clear-full-btn{ width:100%; border-radius:999px; padding:12px 16px; background:transparent; border:1px solid rgba(255,255,255,0.04); color:#9aa5b2; font-weight:600; cursor:pointer }
  .apply-btn{ margin-left:12px; background:#F6255A; color:#fff; border:none; padding:12px 18px; border-radius:999px; font-weight:600; cursor:pointer }
  .apply-btn:disabled{ opacity:0.55; cursor:not-allowed; background: rgba(246,37,90,0.35); color: rgba(255,255,255,0.85); }

        @media (max-width: 600px){
          .filter-panel{ left:0; right:0; top:0; bottom:0; width:100%; height:100%; max-height: none; border-radius:0; }
          .filter-panel-body{ padding:20px; }
        }
      `}</style>
    </div>
  );
};

export default FilterPanel;
